import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const QuickActionToolbar = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [contextActions, setContextActions] = useState([]);
  const location = useLocation();

  // Define context-specific actions based on current route
  const actionsByRoute = {
    '/fleet-dashboard': [
      { id: 'export-fleet', label: 'Export Fleet Data', icon: 'Download', action: 'export' },
      { id: 'schedule-report', label: 'Schedule Report', icon: 'Calendar', action: 'schedule' },
      { id: 'alert-settings', label: 'Alert Settings', icon: 'Bell', action: 'settings' }
    ],
    '/voyage-planning': [
      { id: 'optimize-route', label: 'Optimize Route', icon: 'Route', action: 'optimize' },
      { id: 'save-scenario', label: 'Save Scenario', icon: 'Save', action: 'save' },
      { id: 'compare-routes', label: 'Compare Routes', icon: 'GitCompare', action: 'compare' }
    ],
    '/emissions-calculator': [
      { id: 'calculate-emissions', label: 'Calculate Now', icon: 'Calculator', action: 'calculate' },
      { id: 'export-results', label: 'Export Results', icon: 'FileDown', action: 'export' },
      { id: 'save-calculation', label: 'Save Calculation', icon: 'BookmarkPlus', action: 'save' }
    ],
    '/eua-trading-hub': [
      { id: 'quick-trade', label: 'Quick Trade', icon: 'Zap', action: 'trade', priority: 'high' },
      { id: 'market-alerts', label: 'Set Price Alert', icon: 'TrendingUp', action: 'alert' },
      { id: 'portfolio-view', label: 'Portfolio View', icon: 'PieChart', action: 'view' }
    ],
    '/compliance-reports': [
      { id: 'generate-report', label: 'Generate Report', icon: 'FileText', action: 'generate' },
      { id: 'submit-compliance', label: 'Submit to Authority', icon: 'Send', action: 'submit', priority: 'high' },
      { id: 'audit-trail', label: 'View Audit Trail', icon: 'History', action: 'audit' }
    ],
    '/vessel-performance': [
      { id: 'performance-report', label: 'Performance Report', icon: 'BarChart3', action: 'report' },
      { id: 'benchmark-fleet', label: 'Benchmark Fleet', icon: 'Target', action: 'benchmark' },
      { id: 'efficiency-tips', label: 'Efficiency Tips', icon: 'Lightbulb', action: 'tips' }
    ],
    '/user-management': [
      { id: 'add-user', label: 'Add User', icon: 'UserPlus', action: 'add' },
      { id: 'export-users', label: 'Export Users', icon: 'Users', action: 'export' },
      { id: 'audit-log', label: 'View Audit Log', icon: 'FileSearch', action: 'audit' }
    ]
  };

  // Global actions available on all pages
  const globalActions = [
    { id: 'emergency-stop', label: 'Emergency Stop Trading', icon: 'AlertOctagon', action: 'emergency', priority: 'critical' },
    { id: 'quick-compliance', label: 'Quick Compliance Check', icon: 'Shield', action: 'compliance' },
    { id: 'contact-support', label: 'Contact Support', icon: 'HelpCircle', action: 'support' }
  ];

  useEffect(() => {
    const routeActions = actionsByRoute?.[location?.pathname] || [];
    setContextActions([...routeActions, ...globalActions]);
  }, [location?.pathname]);

  const handleActionClick = (action) => {
    switch (action?.action) {
      case 'export':
        console.log('Exporting data...');
        break;
      case 'calculate': console.log('Starting calculation...');
        break;
      case 'trade': console.log('Opening quick trade...');
        break;
      case 'generate': console.log('Generating report...');
        break;
      case 'emergency': console.log('Emergency stop initiated...');
        break;
      default:
        console.log(`Action: ${action?.action}`);
    }
    setIsExpanded(false);
  };

  const getPriorityStyles = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-error text-error-foreground hover:bg-error/90';
      case 'high':
        return 'bg-warning text-warning-foreground hover:bg-warning/90';
      default:
        return 'bg-primary text-primary-foreground hover:bg-primary/90';
    }
  };

  const getActionIcon = (action) => {
    if (action?.priority === 'critical') return 'AlertOctagon';
    if (action?.priority === 'high') return action?.icon;
    return action?.icon;
  };

  return (
    <div className="fixed bottom-6 right-6 z-1000">
      {/* Expanded Actions */}
      {isExpanded && (
        <div className="mb-4 space-y-2 animate-fade-in">
          {contextActions?.map((action) => (
            <div
              key={action?.id}
              className="flex items-center justify-end"
            >
              <div className="bg-popover border border-border rounded-lg px-3 py-2 mr-3 shadow-maritime-md">
                <span className="text-sm font-medium text-foreground whitespace-nowrap">
                  {action?.label}
                </span>
              </div>
              <Button
                onClick={() => handleActionClick(action)}
                className={`w-12 h-12 rounded-full shadow-maritime-md ${getPriorityStyles(action?.priority)}`}
              >
                <Icon 
                  name={getActionIcon(action)} 
                  size={20} 
                  strokeWidth={2}
                />
              </Button>
            </div>
          ))}
        </div>
      )}
      {/* Main Action Button */}
      <Button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-14 h-14 rounded-full shadow-maritime-lg transition-all duration-300 ${
          isExpanded 
            ? 'bg-muted text-muted-foreground rotate-45' 
            : 'bg-primary text-primary-foreground hover:bg-primary/90'
        }`}
      >
        <Icon 
          name={isExpanded ? 'X' : 'Zap'} 
          size={24} 
          strokeWidth={2}
        />
      </Button>
      {/* Keyboard Shortcut Hint */}
      {!isExpanded && (
        <div className="absolute -top-12 right-0 bg-popover border border-border rounded px-2 py-1 text-xs text-muted-foreground opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
          Press Alt+Q for quick actions
        </div>
      )}
    </div>
  );
};

export default QuickActionToolbar;